import { useCallback, useEffect } from "react";


import { db } from "../../../config/firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import dayjs from "dayjs";
import { useState } from "react";
import Modal from "react-modal";
import { AutoComplete } from "antd";
import InvoicePDF from "../pdf/invoicePDF";
import { toast } from "react-toastify";
import { getUser, getUsers } from "../../../lib/user";
import { getQuote } from "../../../lib/quotes";
import { api } from "../../../config/api";
// import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { incrementInvoiceNumber, getNextInvoiceNumber } from '../../../utils/invoiceNumberUtils';
import DiscountInput from "../../forms/helperComponent/DiscountInput";
// dayjs.extend(isSameOrAfter);

const ORDER_REFERENCE_MAX_LENGTH = 60;
const BOTTOM_NOTE_MAX_LENGTH = 180;
const DESCRIPTION_MAX_LENGTH = 200;
const limitOrderReference = (value) =>
  String(value || "").slice(0, ORDER_REFERENCE_MAX_LENGTH);
const limitBottomNote = (value) =>
  String(value || "").slice(0, BOTTOM_NOTE_MAX_LENGTH);
const limitDescription = (value) =>
  String(value || "").slice(0, DESCRIPTION_MAX_LENGTH);

const isTaxOrDutyItem = (description) => {
  const desc = String(description || "").trim().toLowerCase();
  return (
    desc === "vat" ||
    desc.includes("duty") ||
    desc.includes("duties") ||
    desc.includes("tax")
  );
};

const getExternalItemsTotal = (items) =>
  Number(
    (items || []).reduce(
      (sum, item) => sum + Number(item.qty || 0) * Number(item.price || 0),
      0
    ).toFixed(2)
  );

const getExternalDiscountableSubTotal = (items) =>
  Number(
    (items || []).reduce((sum, item) => {
      if (isTaxOrDutyItem(item?.description)) return sum;
      return sum + Number(item?.qty || 0) * Number(item?.price || 0);
    }, 0).toFixed(2)
  );

const getExternalInvoiceTotals = (items, vatPercentage, discount = null) => {
  const subTotal = getExternalItemsTotal(items);
  const discountableSubTotal = getExternalDiscountableSubTotal(items);
  let discountAmount = 0;
  if (discount) {
    const rawVal = Number(discount.value ?? discount.amount ?? 0);
    if (rawVal > 0) {
      if (discount.type === "percentage") {
        discountAmount = (discountableSubTotal * rawVal) / 100;
      } else {
        discountAmount = rawVal;
      }
      discountAmount = Math.min(discountAmount, discountableSubTotal);
    }
  }
  const discountedSubTotal = Math.max(0, subTotal - discountAmount);
  const vat = Math.max(0, Number(vatPercentage || 0));
  const vatAmount = Number(((subTotal * vat) / 100).toFixed(2));
  const total = Number((discountedSubTotal + vatAmount).toFixed(2));

  return { subTotal, discountableSubTotal, discountAmount, discountedSubTotal, vatAmount, total };
};
const BANK_ACCOUNTS_STORAGE_KEY = "invoiceBankAccounts";
const DEFAULT_BANK_ACCOUNTS = [
  {
    id: "one-union-nl28",
    label: "One Union NL28",
    accountName: "One Union Solutions B.V.",
    iban: "NL28 INGB 0006 9674 14",
    swift: "INGBNL2A",
  },
];

const EXTERNAL_INVOICE_TEMPLATES = [
  {
    label: "IOR",
    items: [
      "IOR",
      "Export documentation",
      "Customs clearance",
      "Handling",
      "Collection",
      "Airfreight",
      "Administration",
      "Duties and taxes",
      "Compliance",
      "VAT",
      "Final delivery",
      "Destination charges",
      "Liability coverage",
      "Approvals",
      "Import permit",
      "Bank fees",
      "Validation",
      "THC",
    ],
  },
  {
    label: "EOR",
    items: [
      "EOR",
      "Export clearance",
      "Export documentation",
      "Collection",
      "Airfreight",
      "Handling",
      "Administration",
      "Bank fees",
      "Export compliance",
    ],
  },
  {
    label: "EOR & IOR",
    items: [
      "EOR",
      "Export documentation",
      "IOR",
      "Customs clearance",
      "Handling",
      "Collection",
      "Airfreight",
      "Administration",
      "Duties and taxes",
      "Compliance",
      "VAT",
      "Final delivery",
      "Destination charges",
      "Liability coverage",
      "Approvals",
      "Import permit",
      "Bank fees",
      "Validation",
      "THC",
    ],
  },
];

const loadBankAccounts = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(BANK_ACCOUNTS_STORAGE_KEY));
    return Array.isArray(stored) && stored.length ? stored : DEFAULT_BANK_ACCOUNTS;
  } catch (_) {
    return DEFAULT_BANK_ACCOUNTS;
  }
};

const saveBankAccounts = (accounts) => {
  localStorage.setItem(BANK_ACCOUNTS_STORAGE_KEY, JSON.stringify(accounts));
};

const RequiredLabel = ({ children }) => (
  <span>
    {children} <span className="text-red-600">*</span>
  </span>
);

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "calc(100vw - 48px)",
    maxWidth: "1160px",
    maxHeight: "94vh",
    overflowY: "auto",
    padding: "28px 32px",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.79)",
    zIndex: 100,
  },
};
const shipmentInvoiceStyles = {
  ...customStyles,
  content: {
    ...customStyles.content,
    width: "min(780px, calc(100vw - 32px))",
    maxWidth: "calc(100vw - 32px)",
    maxHeight: "86vh",
    padding: "24px 28px",
  },
};
const InvoiceModal = ({
  setModalOpen,
  modalOpen,
  shipmentData,
  setShipmentData,
  orderOptions = [],
  requireOrderSelection = false,
  externalInvoiceMode = false,
  onInvoiceCreated,
}) => {
  const [loading, setLoading] = useState(false);
  const [invoiceClientPreview, setInvoiceClientPreview] = useState(null);
  const [bankAccounts, setBankAccounts] = useState(DEFAULT_BANK_ACCOUNTS);
  const [orderDropdownOpen, setOrderDropdownOpen] = useState(false);
  const [orderSearch, setOrderSearch] = useState("");
  const [bankDropdownOpen, setBankDropdownOpen] = useState(false);
  const [selectedExternalTemplate, setSelectedExternalTemplate] = useState("");
  const [externalDiscountType, setExternalDiscountType] = useState("percentage");
  const [externalDiscountValue, setExternalDiscountValue] = useState("");
  const [showBankAccountForm, setShowBankAccountForm] = useState(false);
  const [nameOptions, setNameOptions] = useState([]);
  const [loadingNameOptions, setLoadingNameOptions] = useState(false);
  const [newBankAccount, setNewBankAccount] = useState({
    label: "",
    accountName: "One Union Solutions B.V.",
    iban: "",
    swift: "",
  });
  const today = new Date().toISOString().split('T')[0];
  const [data, setData] = useState({
    name: "",
    invoiceDate: today,
    dueDays: "",
    invoiceNo: "",
    currency: "USD",
    total: "",
    vatPercentage: "",
    paid: 0,
    status: "Not Paid",
    selectedBankAccountId: bankAccounts[0]?.id || DEFAULT_BANK_ACCOUNTS[0].id,
    orderReference: limitOrderReference(shipmentData?.loadAddress_ReferenceCustomer),
    collectionLocation: "",
    deliveryLocation: "",
    serviceRecipientText: "",
    externalBillToText: "",
    billToText: "",
    description: "",
    items: [{ description: "", qty: 1, price: "" }],
    bottomNote: "",
  });

  useEffect(() => {
    if (!modalOpen || !externalInvoiceMode) return;

    let cancelled = false;

    const fetchNameOptions = async () => {
      setLoadingNameOptions(true);
      try {
        const [firestoreUsersResult, invoicesResult] = await Promise.allSettled([
          getUsers(),
          api.get("/invoice"),
        ]);

        const namesByKey = new Map();

        const addName = (rawName, user = null) => {
          const name = String(rawName || "").trim();
          if (!name) return;
          const key = name.toLowerCase();
          const existing = namesByKey.get(key);
          // Keep the user record when a name is present in both users and
          // previously-created invoices. Invoice-only names remain available
          // as custom names, but do not populate customer fields.
          if (!existing || (user && !existing.user)) {
            namesByKey.set(key, { name, user: user || existing?.user || null });
          }
        };

        if (firestoreUsersResult.status === "fulfilled") {
          (firestoreUsersResult.value || []).forEach((user) => addName(user?.name, user));
        } else {
          console.warn("Failed to fetch Firestore users for invoice names:", firestoreUsersResult.reason);
        }

        if (invoicesResult.status === "fulfilled") {
          const invoices = invoicesResult.value?.data;
          (Array.isArray(invoices) ? invoices : []).forEach((invoice) => addName(invoice?.name));
        } else {
          console.warn("Failed to fetch invoice names:", invoicesResult.reason);
        }

        if (!cancelled) {
          setNameOptions(
            Array.from(namesByKey.values())
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(({ name, user }) => ({ value: name, label: name, user }))
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingNameOptions(false);
        }
      }
    };

    fetchNameOptions();

    return () => {
      cancelled = true;
    };
  }, [modalOpen, externalInvoiceMode]);

  useEffect(() => {
    const fetchBankAccounts = async () => {
      try {
        const res = await api.get("/bank-accounts");
        const dbAccounts = res.data;

        const localStored = localStorage.getItem("invoiceBankAccounts");
        let localAccounts = [];
        if (localStored) {
          try {
            const parsed = JSON.parse(localStored);
            if (Array.isArray(parsed)) {
              localAccounts = parsed.filter(acc => acc.id !== "one-union-nl28");
            }
          } catch (_) {}
        }

        let finalDbAccounts = dbAccounts;
        if (localAccounts.length > 0) {
          try {
            const syncRes = await api.post("/bank-accounts", localAccounts);
            localStorage.removeItem("invoiceBankAccounts");
            finalDbAccounts = syncRes.data;
          } catch (err) {
            console.error("Failed to sync local bank accounts to database:", err);
            finalDbAccounts = [...dbAccounts, ...localAccounts];
          }
        }

        const merged = [...DEFAULT_BANK_ACCOUNTS];
        finalDbAccounts.forEach(acc => {
          const exists = merged.some(m => m.iban.trim().toLowerCase() === acc.iban.trim().toLowerCase());
          if (!exists) {
            merged.push({
              id: acc._id || acc.id,
              label: acc.label,
              accountName: acc.accountName,
              iban: acc.iban,
              swift: acc.swift,
            });
          }
        });

        setBankAccounts(merged);
        
        setData((prevData) => {
          const validSelected = merged.some(acc => acc.id === prevData.selectedBankAccountId);
          if (!validSelected) {
            return {
              ...prevData,
              selectedBankAccountId: merged[0]?.id || DEFAULT_BANK_ACCOUNTS[0].id,
            };
          }
          return prevData;
        });
      } catch (error) {
        console.error("Error loading bank accounts:", error);
        const stored = localStorage.getItem("invoiceBankAccounts");
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            if (Array.isArray(parsed) && parsed.length) {
              setBankAccounts(parsed);
              return;
            }
          } catch (_) {}
        }
        setBankAccounts(DEFAULT_BANK_ACCOUNTS);
      }
    };

    if (modalOpen) {
      fetchBankAccounts();
    }
  }, [modalOpen]);

  const showOrderDropdown = requireOrderSelection;
  const getOrderDisplayName = (shipment) =>
    shipment?.name ||
    shipment?.companyName ||
    shipment?.company ||
    shipment?.clientName ||
    shipment?.customerName ||
    shipment?.businessName ||
    shipment?.userName ||
    "Unknown";
  const selectedOrderLabel = shipmentData?.orderNumber
    ? `${shipmentData.orderNumber} - ${getOrderDisplayName(shipmentData)}`
    : "Select order number";
  const filteredOrderOptions = Array.isArray(orderOptions)
    ? orderOptions
        .filter((shipment) => shipment?.orderNumber)
        .filter((shipment) => {
          const search = orderSearch.trim().toLowerCase();
          if (!search) return true;
          return [
            shipment?.orderNumber,
            shipment?.name,
            shipment?.companyName,
            shipment?.company,
            shipment?.clientName,
            shipment?.customerName,
            shipment?.businessName,
            shipment?.userName,
          ]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(search));
        })
    : [];


  const isFormValid = () => {
    return (
      (!showOrderDropdown || shipmentData?.id) &&
      (!externalInvoiceMode || data.name.trim() !== "") &&
      (!externalInvoiceMode || (data.total !== "" && Number(data.total) >= 0)) &&
      (!externalInvoiceMode || Number(data.paid || 0) <= Number(data.total || 0)) &&
      data.dueDays > 0 &&
      String(data.invoiceNo || "").trim() !== "" &&
      data.selectedBankAccountId &&
      data.orderReference.trim() !== "" &&
      data.externalBillToText.trim() !== "" &&
      (!externalInvoiceMode ||
        (data.items || []).some(
          (item) =>
            item.description.trim() !== "" &&
            Number(item.qty || 0) > 0 &&
            Number(item.price || 0) >= 0
        )) &&
      data.description.trim() !== ""
    );
  };

  const getFallbackClient = useCallback((shipment) => ({
    name: shipment?.name || shipment?.companyName || "Unknown Client",
    address: shipment?.address || "",
    postalCode: shipment?.postalCode || "",
    city: shipment?.city || "",
    country: shipment?.country || "",
    vatNumber: shipment?.vatNumber || "",
  }), []);

  const getInvoiceClient = useCallback(async (shipment) => {
    if (!shipment?.userId) {
      toast.warning("Linked user is missing. Invoice will use shipment details.");
      return getFallbackClient(shipment);
    }

    try {
      return await getUser(shipment.userId);
    } catch (error) {
      console.warn("Linked user not found for invoice:", shipment.userId, error);
      toast.warning("Linked user not found. Invoice will use shipment details.");
      return getFallbackClient(shipment);
    }
  }, [getFallbackClient]);

  const formatClientForBillTo = (client) => {
    if (!client) return "";
    // A customer-specific Bill To value takes precedence when copying the customer.
    if (client?.billToText?.trim()) return client.billToText.trim();
    return [
      client?.name,
      client?.address,
      [client?.postalCode, client?.city].filter(Boolean).join(" "),
      client?.country,
      client?.vatNumber,
    ]
      .filter(Boolean)
      .join("\n");
  };

  const preventNumberWheelChange = (e) => {
    // Native number/date inputs can change value on wheel when focused.
    e.currentTarget.blur();
  };

  const getExternalDiscountData = (
    itemsList = data.items,
    discType = externalDiscountType,
    discVal = externalDiscountValue
  ) => {
    const discountableSubTotal = getExternalDiscountableSubTotal(itemsList);
    const rawDiscount = parseFloat(discVal) || 0;
    let discountAmount = 0;
    if (rawDiscount > 0) {
      if (discType === "percentage") {
        discountAmount = (discountableSubTotal * rawDiscount) / 100;
      } else {
        discountAmount = rawDiscount;
      }
      discountAmount = Math.min(discountAmount, discountableSubTotal);
    }
    return discountAmount > 0
      ? {
          type: discType,
          value: rawDiscount,
          amount: parseFloat(discountAmount.toFixed(2)),
          servicesSubtotalBeforeDiscount: discountableSubTotal,
        }
      : null;
  };

  const calculateExternalTotalsState = (
    items,
    vatPercentage,
    discType = externalDiscountType,
    discVal = externalDiscountValue,
    currentPaid = data.paid,
    currentStatus = data.status
  ) => {
    const discountData = getExternalDiscountData(items, discType, discVal);
    const { total } = getExternalInvoiceTotals(items, vatPercentage, discountData);
    const paid = Math.min(Number(currentPaid || 0), total);
    const status = total > 0 && paid >= total ? "Paid" : currentStatus === "Paid" ? "Not Paid" : currentStatus;

    return {
      total: total ? String(total) : "",
      paid,
      status,
    };
  };

  const handleExternalDiscountTypeChange = (type) => {
    setExternalDiscountType(type);
    setExternalDiscountValue("");
    const calc = calculateExternalTotalsState(data.items, data.vatPercentage, type, "", data.paid, data.status);
    setData((prev) => ({ ...prev, ...calc }));
  };

  const handleExternalDiscountValueChange = (val) => {
    setExternalDiscountValue(val);
    const calc = calculateExternalTotalsState(data.items, data.vatPercentage, externalDiscountType, val, data.paid, data.status);
    setData((prev) => ({ ...prev, ...calc }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // if (name === "dueDays" && (value <= 0 || !Number.isInteger(Number(value)))) {
    //   toast.warning("Please enter a positive whole number for due days");
    //   return;
    // }
    if (externalInvoiceMode && name === "vatPercentage") {
      setData((prevData) => {
        const calc = calculateExternalTotalsState(
          prevData.items,
          value,
          externalDiscountType,
          externalDiscountValue,
          prevData.paid,
          prevData.status
        );

        return {
          ...prevData,
          vatPercentage: value,
          ...calc,
        };
      });
      return;
    }

    if (externalInvoiceMode && name === "paid") {
      setData((prevData) => {
        const total = Number(prevData.total || 0);
        const paid = value === "" ? "" : Math.min(Math.max(Number(value || 0), 0), total);

        return {
          ...prevData,
          paid,
          status: total > 0 && Number(paid || 0) >= total ? "Paid" : prevData.status === "Paid" ? "Not Paid" : prevData.status,
        };
      });
      return;
    }

    setData((prevData) => ({
      ...prevData,
      [name]:
        name === "orderReference"
          ? limitOrderReference(value)
          : name === "bottomNote"
          ? limitBottomNote(value)
          : name === "description"
          ? limitDescription(value)
          : value,
    }));



    // if (name === "dueDate" || name === "invoiceDate") {
    //   const invoiceDate = name === "invoiceDate" ? value : data.invoiceDate;
    //   const dueDate = name === "dueDate" ? value : data.dueDate;

    //   if (invoiceDate && dueDate && dayjs(dueDate).isBefore(dayjs(invoiceDate))) {
    //     toast.warning("Due date is earlier than the invoice date.");
    //   }
    // }
  };

  const handleExternalNameChange = (value) => {
    const normalizedName = String(value || "").trim().toLowerCase();
    const selectedUser = nameOptions.find(
      (option) => String(option.value).trim().toLowerCase() === normalizedName
    )?.user;
    const customerBillTo = String(selectedUser?.billToText || "").trim();

    setInvoiceClientPreview(selectedUser || null);
    setData((prevData) => ({
      ...prevData,
      name: value,
      // Only a user's explicitly saved Bill To text should be copied here.
      // A custom/invoice-only name or user without this value leaves it blank.
      externalBillToText: customerBillTo,
    }));
  };

  const handleExternalItemChange = (index, field, value) => {
    setData((prevData) => {
      const items = [...(prevData.items || [])];
      items[index] = {
        ...items[index],
        [field]:
          field === "description"
            ? limitDescription(value)
            : value,
      };
      const calc = calculateExternalTotalsState(
        items,
        prevData.vatPercentage,
        externalDiscountType,
        externalDiscountValue,
        prevData.paid,
        prevData.status
      );

      return {
        ...prevData,
        items,
        ...calc,
      };
    });
  };

  const handleAddExternalItem = () => {
    setData((prevData) => ({
      ...prevData,
      items: [...(prevData.items || []), { description: "", qty: 1, price: "" }],
    }));
  };

  const handleRemoveExternalItem = (index) => {
    setData((prevData) => {
      const nextItems = (prevData.items || []).filter((_, itemIndex) => itemIndex !== index);
      const items = nextItems.length ? nextItems : [{ description: "", qty: 1, price: "" }];
      const calc = calculateExternalTotalsState(
        items,
        prevData.vatPercentage,
        externalDiscountType,
        externalDiscountValue,
        prevData.paid,
        prevData.status
      );

      return {
        ...prevData,
        items,
        ...calc,
      };
    });
  };

  const handleExternalTemplateChange = (event) => {
    const templateLabel = event.target.value;
    setSelectedExternalTemplate(templateLabel);

    if (!templateLabel) {
      return;
    }

    const selectedTemplate = EXTERNAL_INVOICE_TEMPLATES.find(
      (template) => template.label === templateLabel
    );

    if (!selectedTemplate) {
      return;
    }

    const items = selectedTemplate.items.map((description) => ({
      description: limitDescription(description),
      qty: 1,
      price: "",
    }));

    setData((prevData) => {
      const calc = calculateExternalTotalsState(
        items,
        prevData.vatPercentage,
        externalDiscountType,
        externalDiscountValue,
        prevData.paid,
        prevData.status
      );

      return {
        ...prevData,
        items,
        ...calc,
      };
    });
  };

  const handleClearExternalTemplate = () => {
    setSelectedExternalTemplate("");
    setData((prevData) => {
      const items = [{ description: "", qty: 1, price: "" }];
      const calc = calculateExternalTotalsState(
        items,
        prevData.vatPercentage,
        externalDiscountType,
        externalDiscountValue,
        0,
        prevData.status
      );
      return {
        ...prevData,
        items,
        ...calc,
      };
    });
  };

  const handleOrderNumberChange = (e) => {
    const selectedShipment = orderOptions.find(
      (shipment) => String(shipment?.id) === String(e?.target?.value || e)
    );

    setSelectedExternalTemplate("");
    setShipmentData(selectedShipment || null);
    setData((prevData) => ({
      ...prevData,
      orderReference: limitOrderReference(selectedShipment?.loadAddress_ReferenceCustomer),
      collectionLocation: "",
      deliveryLocation: "",
      serviceRecipientText: "",
      externalBillToText: "",
      billToText: "",
      description: "",
      items: [{ description: "", qty: 1, price: "" }],
      bottomNote: "",
    }));
    setInvoiceClientPreview(null);

    if (selectedShipment) {
      getInvoiceClient(selectedShipment).then(setInvoiceClientPreview);
    }
    setOrderSearch("");
    setOrderDropdownOpen(false);
  };

  const handleCopyBillToFromCustomer = () => {
    if (externalInvoiceMode) {
      setData((prevData) => ({
        ...prevData,
        serviceRecipientText: prevData.externalBillToText,
        billToText: prevData.externalBillToText,
      }));
      return;
    }

    const client = invoiceClientPreview || getFallbackClient(shipmentData);
    setData((prevData) => ({
      ...prevData,
      billToText: formatClientForBillTo(client),
    }));
  };

  const handleClearBillTo = () => {
    setData((prevData) => ({
      ...prevData,
      serviceRecipientText: externalInvoiceMode ? "" : prevData.serviceRecipientText,
      billToText: "",
    }));
  };

  const handleNewBankAccountChange = (e) => {
    const { name, value } = e.target;
    setNewBankAccount((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleAddBankAccount = async () => {
    const label = newBankAccount.label.trim();
    const iban = newBankAccount.iban.trim();
    const accountName = newBankAccount.accountName.trim();

    if (!label || !iban || !accountName) {
      toast.error("Bank name, account name, and IBAN are required.");
      return;
    }

    try {
      setLoading(true);
      const res = await api.post("/bank-accounts", {
        label,
        accountName,
        iban,
        swift: newBankAccount.swift.trim(),
      });

      const newAcc = {
        id: res.data._id,
        label: res.data.label,
        accountName: res.data.accountName,
        iban: res.data.iban,
        swift: res.data.swift,
      };

      setBankAccounts((prev) => [...prev, newAcc]);
      setData((prevData) => ({
        ...prevData,
        selectedBankAccountId: newAcc.id,
      }));
      setNewBankAccount({
        label: "",
        accountName: "One Union Solutions B.V.",
        iban: "",
        swift: "",
      });
      setShowBankAccountForm(false);
      toast.success("Bank account added successfully!");
    } catch (error) {
      console.error("Error adding bank account:", error);
      toast.error(error.response?.data?.message || "Failed to add bank account.");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveBankAccount = async () => {
    if (bankAccounts.length <= 1) {
      toast.error("At least one bank account is required.");
      return;
    }

    if (data.selectedBankAccountId === "one-union-nl28") {
      toast.error("Default bank account cannot be removed.");
      return;
    }

    try {
      setLoading(true);
      await api.delete(`/bank-accounts/${data.selectedBankAccountId}`);

      const updatedAccounts = bankAccounts.filter(
        (account) => account.id !== data.selectedBankAccountId
      );
      const nextSelectedAccount = updatedAccounts[0];
      setBankAccounts(updatedAccounts);
      setData((prevData) => ({
        ...prevData,
        selectedBankAccountId: nextSelectedAccount?.id || "",
      }));
      toast.success("Bank account removed successfully!");
    } catch (error) {
      console.error("Error removing bank account:", error);
      toast.error(error.response?.data?.message || "Failed to remove bank account.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectBankAccount = (accountId) => {
    setData((prevData) => ({
      ...prevData,
      selectedBankAccountId: accountId,
    }));
    setBankDropdownOpen(false);
  };

  const selectedBankAccount =
    bankAccounts.find((account) => account.id === data.selectedBankAccountId) ||
    bankAccounts[0] ||
    DEFAULT_BANK_ACCOUNTS[0];

  // const isDateValid = () => {
  //   if (!data.invoiceDate || !data.dueDate) return true;
  //   return dayjs(data.dueDate).isSameOrAfter(dayjs(data.invoiceDate));
  // };

  // const handleSubmit = async (e) => {
  //   e.preventDefault();

  //   if (!isFormValid()) {
  //     toast.error("All fields are mandatory and cannot be empty. Please fill in all the fields.");
  //     return;
  //   }

  //   if (!isDateValid()) {
  //     toast.error("Due date cannot be earlier than the invoice date.");
  //     return;
  //   }

  //   const emptyFields = Object.entries(data).filter(([key, value]) => !value);

  //   if (emptyFields.length > 0) {
  //     // Show error message for empty fields
  //     toast.error("All fields are mandatory. Please fill in all the fields.");
  //     return;
  //   }

  //   setLoading(true);
  //   const invoiceDate = dayjs(data?.invoiceDate);
  //   const dueDate = dayjs(data?.dueDate);
  //   const paymentWithin = dueDate.diff(invoiceDate, "day");
  //   const client = await getUser(shipmentData?.userId);

  //   const dataToSend = {
  //     ...data,
  //     paymentWithin,
  //     client,
  //     shipment: shipmentData,
  //   };
  //   // console.log("data..", dataToSend);
  //   try {
  //     await api.post("/invoice", {
  //       ...data,
  //       userId: shipmentData?.userId,
  //       shipmentId: shipmentData?.id,
  //       orderNumber: shipmentData?.orderNumber,
  //     });
  //     const blob = await InvoicePDF(dataToSend);
  //     const link = document.createElement("a");
  //     link.href = URL.createObjectURL(blob);
  //     link.download = `Invoice_${dataToSend?.invoiceNo}.pdf`;
  //     link.click();
  //     toast.success("Invoice created successfully!");
  //     setShipmentData(null);
  //     setModalOpen(false);
  //   } catch (error) {
  //     console.error("Error generating invoice:", error);
  //     toast.error("Invoice not generated");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  useEffect(() => {
    const fetchInvoiceNumber = async () => {
      const nextNumber = await getNextInvoiceNumber();
      setData(prev => ({
        ...prev,
        invoiceNo: nextNumber,
        invoiceDate: today
      }));
    };

    if (modalOpen) {
      if (externalInvoiceMode) {
        setSelectedExternalTemplate("");
        setExternalDiscountType("percentage");
        setExternalDiscountValue("");
        fetchInvoiceNumber();
        setData((prev) => ({
          ...prev,
          name: "",
          invoiceNo: "",
          invoiceDate: today,
          currency: "USD",
          dueDays: "",
          total: "",
          vatPercentage: "",
          paid: 0,
          status: "Not Paid",
          orderReference: "",
          collectionLocation: "",
          deliveryLocation: "",
          serviceRecipientText: "",
          externalBillToText: "",
          billToText: "",
          description: "",
          items: [{ description: "", qty: 1, price: "" }],
          bottomNote: "",
        }));
      } else {
        fetchInvoiceNumber();
      }
      if (shipmentData?.id) {
        getInvoiceClient(shipmentData).then((client) => {
          setInvoiceClientPreview(client);
          const customerBillTo = client?.billToText?.trim() || formatClientForBillTo(client);
          setData((prevData) =>
            prevData.externalBillToText || !customerBillTo
              ? prevData
              : { ...prevData, externalBillToText: customerBillTo }
          );
        });
      } else {
        setInvoiceClientPreview(null);
      }
    }
  }, [externalInvoiceMode, getInvoiceClient, modalOpen, today, shipmentData]);



  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid()) {
      toast.error("All fields are mandatory and cannot be empty. Please fill in all the fields.");
      return;
    }

    if (!externalInvoiceMode && !shipmentData?.id) {
      toast.error("Please select an order number.");
      return;
    }

    // if (!isDateValid()) {
    //   toast.error("Due date cannot be earlier than the invoice date.");
    //   return;
    // }

    setLoading(true);
    // Calculate due date including weekends (calendar days)
    const invoiceDate = dayjs(data.invoiceDate);
    const dueDate = invoiceDate.add(Number(data.dueDays), 'day');
    const paymentWithin = dueDate.diff(invoiceDate, "day");

    if (externalInvoiceMode) {
      try {
        const externalItems = (data.items || [])
          .map((item) => ({
            description: item.description.trim(),
            qty: Number(item.qty || 0),
            price: Number(item.price || 0),
          }))
          .filter((item) => item.description);
        const externalDiscount = getExternalDiscountData(externalItems, externalDiscountType, externalDiscountValue);
        const externalTotals = getExternalInvoiceTotals(externalItems, data.vatPercentage, externalDiscount);
        const response = await api.post("/invoice/external", {
          ...data,
          invoiceNo: String(data.invoiceNo || "").trim(),
          orderNumber: null,
          externalBillToText: data.externalBillToText,
          total: externalTotals.total,
          vatPercentage: Number(data.vatPercentage || 0),
          vatAmount: externalTotals.vatAmount,
          discount: externalDiscount,
          paid: Number(data.paid || 0),
          description: data.description.trim(),
          items: externalItems,
          selectedBankAccount,
          invoiceSource: "external",
          isExternalInvoice: true,
        });

        toast.success("Invoice created successfully!");
        onInvoiceCreated?.(response?.data?.data);
        setShipmentData(null);
        setModalOpen(false);
      } catch (error) {
        console.error("Error generating external invoice:", error);
        const message = error?.response?.data?.message || error?.response?.data?.errors?.[0];
        toast.error(message || "Invoice not generated");
      } finally {
        setLoading(false);
      }
      return;
    }

    const client = await getInvoiceClient(shipmentData);

    // We'll compute monetary snapshot (subTotal, VAT, insurance, total) here
    // so the invoice collection and PDF both reflect correct values.
    const items = Array.isArray(shipmentData?.services)
      ? shipmentData.services.map(s => ({
          description: s?.description || "",
          qty: Number(s?.numberOfItem || 1),
          price: Number(s?.exclVAT || 0),
        }))
      : [];
    const subTotal = items.reduce((sum, it) => sum + Number(it.qty || 0) * Number(it.price || 0), 0);
    // Prefer VAT from shipment if provided, else 0
    const vatPercentageSnapshot = Number(shipmentData?.vatPercentage || 0);
    const vatAmountSnapshot = vatPercentageSnapshot > 0 ? Number(((subTotal * vatPercentageSnapshot) / 100).toFixed(2)) : 0;
    const insuranceCheckedSnapshot = !!shipmentData?.isInsuranceChecked;

    try {
      // Pull CIF value from the related quote (shipment) if available
      let cifValueToSave;
      let quoteData;
      try {
        if (shipmentData?.id) {
          quoteData = await getQuote(shipmentData.id);
          const rawCif = quoteData?.cifValue?.value;
          if (rawCif !== undefined && rawCif !== null && rawCif !== "") {
            const parsed = Number(rawCif);
            if (!isNaN(parsed)) cifValueToSave = parsed;
          }
        }
      } catch (_) {
        // Non-blocking: proceed without CIF if fetch fails
      }

      // Compute insurance using CIF when available; otherwise fallback to subtotal
      const baseForInsurance = Number(cifValueToSave || 0) > 0 ? Number(cifValueToSave) : Number(subTotal);
      const insuranceAmountSnapshot = insuranceCheckedSnapshot ? Number(((baseForInsurance * 0.0075)).toFixed(2)) : 0;
      const discountSnapshot = shipmentData?.discount || quoteData?.discount || shipmentData?.tradeOSData?.discount || quoteData?.tradeOSData?.discount || null;
      const discountAmountSnapshot = discountSnapshot?.amount > 0 ? Number(discountSnapshot.amount) : 0;
      const discountedSubTotal = Math.max(0, Number(subTotal) - discountAmountSnapshot);
      const totalSnapshot = Number((discountedSubTotal + Number(vatAmountSnapshot) + Number(insuranceAmountSnapshot)).toFixed(2));

      // Build the shipment snapshot that PDF renderer expects
      const shipmentSnapshot = {
        ...shipmentData,
        services: shipmentData?.services || [],
        subTotal,
        discount: discountSnapshot,
        vatPercentage: vatPercentageSnapshot,
        vatAmount: vatAmountSnapshot,
        isInsuranceChecked: insuranceCheckedSnapshot,
        insurance: insuranceAmountSnapshot,
        total: totalSnapshot,
      };

      const dataToSend = {
        ...data,
        dueDate,
        paymentWithin,
        client,
        discount: discountSnapshot,
        shipment: shipmentSnapshot,
        // Provide top-level flags in case templates use them
        isInsuranceChecked: insuranceCheckedSnapshot,
        insuranceAmount: insuranceAmountSnapshot,
        selectedBankAccount,
      };
      // First attempt to create invoice on server; server enforces uniqueness
      const response = await api.post("/invoice", {
        ...data,
        dueDate: dueDate.format('YYYY-MM-DD'),
        userId: shipmentData?.userId,
        shipmentId: shipmentData?.id,
        orderNumber: shipmentData?.orderNumber || null,
        name: getOrderDisplayName(shipmentData),
        currency: shipmentData?.currency || 'USD',
        // Monetary fields computed here to avoid stale/incorrect values
        total: totalSnapshot,
        remaining: totalSnapshot, // paid is 0 on creation
        // CIF and calculation details
        cifValue: cifValueToSave,
        vatPercentage: vatPercentageSnapshot,
        vatAmount: vatAmountSnapshot,
        isInsuranceChecked: insuranceCheckedSnapshot,
        insuranceAmount: insuranceAmountSnapshot,
        discount: discountSnapshot,
        selectedBankAccount,
        // Persist line items
        items,
        invoiceSource: "shipment",
        isExternalInvoice: false,
      });

      try {
        const settingsDocRef = doc(db, "settings", "settings");
        const settingsDoc = await getDoc(settingsDocRef);
        const currentSettings = settingsDoc.exists() ? settingsDoc.data() : {};
        const nextInvoiceNumber = incrementInvoiceNumber(data.invoiceNo);

        await setDoc(settingsDocRef, {
          ...currentSettings,
          invoiceNumber_Prefix: nextInvoiceNumber
        });
      } catch (settingsError) {
        console.error("Invoice created, but invoice number counter was not updated:", settingsError);
        toast.warning("Invoice created, but invoice number counter was not updated");
      }


      console.log("Created invoice:", response.data);

      const blob = await InvoicePDF(dataToSend);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Invoice_${dataToSend?.invoiceNo}.pdf`;
      link.click();

      if (client?.id && shipmentData?.userId) {
        await api.post("/notifications", {
          userId: shipmentData?.userId,
          title: "New Invoice Generated",
          message: `A new invoice (${dataToSend.invoiceNo}) has been generated for your shipment.`,
          linkText: "View Invoice",
          link: `/myinvoices?invoiceNo=${dataToSend.invoiceNo}`,
          role: "user",
          type: "invoice-generated",
          refId: shipmentData?.id,
        });
      }

      // ⭐ store invoice for highlight
      const existing = JSON.parse(localStorage.getItem("highlightInvoices")) || [];
      existing.push(dataToSend.invoiceNo);
      localStorage.setItem("highlightInvoices", JSON.stringify(existing));

      toast.success("Invoice created successfully!");
      onInvoiceCreated?.(response?.data?.data);

      setShipmentData(null);
      setModalOpen(false);


    } catch (error) {
      console.error("Error generating invoice:", error);
      const message = error?.response?.data?.message;
      if (error?.response?.status === 409) {
        toast.error(message || "Invoice number already exists. Please choose a different number.");
      } else if (error?.response?.status === 400) {
        toast.error(message || "Invoice number is required.");
      } else {
        toast.error("Invoice not generated");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={modalOpen}
      onRequestClose={() => setModalOpen(false)}
      style={externalInvoiceMode ? customStyles : shipmentInvoiceStyles}
      contentLabel="Example Modal"
    >
      <div className="w-full">
        <h1 className="text-[#000] font-semibold text-[24px]">
          Generate Invoice
        </h1>

        <form
          className={
            externalInvoiceMode
              ? "mt-5 grid w-full grid-cols-1 gap-x-4 gap-y-4 xl:grid-cols-12"
              : "mt-5 w-full space-y-5"
          }
          onSubmit={handleSubmit}
        >
          {externalInvoiceMode && (
            <>
              <div className="flex flex-col gap-2 xl:col-span-6">
                <label className="w-full text-sm font-normal" htmlFor="name">
                  <RequiredLabel>Name</RequiredLabel>
                </label>
                <AutoComplete
                  id="name"
                  className="w-full"
                  options={nameOptions}
                  value={data.name}
                  onChange={handleExternalNameChange}
                  filterOption={(inputValue, option) =>
                    String(option?.value || "")
                      .toLowerCase()
                      .includes(String(inputValue || "").toLowerCase())
                  }
                  allowClear
                  notFoundContent={
                    loadingNameOptions ? "Loading names..." : "Type a custom name"
                  }
                >
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder={
                      loadingNameOptions
                        ? "Loading names..."
                        : "Select or enter user / company name"
                    }
                    className="px-3 py-2.5 focus:outline-none w-full border rounded-lg border-[#053F92] text-[rgba(5, 63, 146, 0.85)]"
                    autoComplete="off"
                  />
                </AutoComplete>
              </div>
            </>
          )}
          {showOrderDropdown && (
            <div className="flex flex-col gap-2">
              <label className="w-full text-sm font-normal" htmlFor="orderNumber">
                <RequiredLabel>Order Number</RequiredLabel>
              </label>
              <div className="relative">
                <button
                  type="button"
                  id="orderNumber"
                  className={`flex w-full items-center justify-between gap-3 rounded-lg border bg-white px-3 py-2.5 text-left focus:outline-none focus:ring-2 focus:ring-[#053F92]/20 ${
                    shipmentData?.id
                      ? "border-[#053F92] text-[rgba(5,63,146,0.9)]"
                      : "border-[#053F92] text-slate-400"
                  }`}
                  onClick={() => setOrderDropdownOpen((value) => !value)}
                  aria-haspopup="listbox"
                  aria-expanded={orderDropdownOpen}
                >
                  <span className="min-w-0 flex-1 truncate">{selectedOrderLabel}</span>
                  <span className={`shrink-0 text-[#053F92] transition-transform ${orderDropdownOpen ? "rotate-180" : ""}`}>
                    &#9662;
                  </span>
                </button>
                {orderDropdownOpen && (
                  <div
                    className="absolute z-30 mt-1 w-full rounded-lg border border-[#053F92]/30 bg-white p-2 shadow-xl"
                    role="listbox"
                  >
                    <input
                      type="text"
                      className="mb-2 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#053F92] focus:ring-2 focus:ring-[#053F92]/15"
                      placeholder="Search order number or customer"
                      value={orderSearch}
                      onChange={(event) => setOrderSearch(event.target.value)}
                      autoFocus
                    />
                    <div className="max-h-56 overflow-y-auto">
                      {filteredOrderOptions.length ? (
                        filteredOrderOptions.map((shipment) => {
                          const isSelected = shipmentData?.id === shipment.id;
                          return (
                            <button
                              key={shipment.id}
                              type="button"
                              className={`flex w-full flex-col rounded-md px-3 py-2 text-left text-sm transition ${
                                isSelected
                                  ? "bg-[#053F92] text-white"
                                  : "text-slate-800 hover:bg-blue-50"
                              }`}
                              onClick={() => handleOrderNumberChange(shipment.id)}
                              role="option"
                              aria-selected={isSelected}
                            >
                              <span className="font-medium">{shipment.orderNumber}</span>
                              <span className={isSelected ? "text-white/85" : "text-slate-500"}>
                                {getOrderDisplayName(shipment)}
                              </span>
                            </button>
                          );
                        })
                      ) : (
                        <div className="px-3 py-4 text-center text-sm text-slate-500">
                          No order numbers found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <div className={externalInvoiceMode ? "w-full grid grid-cols-1 md:grid-cols-2 gap-3 xl:col-span-6" : "w-full grid grid-cols-1 md:grid-cols-2 gap-3"}>
            <div className="flex flex-col gap-2">
              <label
                className="w-full text-sm font-normal"
                htmlFor="invoiceDate"
              >
                <RequiredLabel>Invoice Date</RequiredLabel>
              </label>
              <input
                type="date"
                className="px-3 py-2.5 focus:outline-none w-full border rounded-lg border-[#053F92] text-[rgba(5, 63, 146, 0.85)]"
                id="invoiceDate"
                value={data.invoiceDate}
                name="invoiceDate"
                onChange={handleInputChange}
                onWheel={preventNumberWheelChange}
              />
            </div>
                <div className="flex flex-col gap-2">
                  <label className="w-full text-sm font-normal" htmlFor="dueDate">
                    <RequiredLabel>Due Days</RequiredLabel>
                  </label>
                  <input
                    type="number"
                    min={1}
                    placeholder="Enter number of Days"
                    className="px-3 py-2.5 focus:outline-none w-full border rounded-lg border-[#053F92] text-[rgba(5, 63, 146, 0.85)]"
                    id="dueDays"
                    onChange={handleInputChange}
                    onWheel={preventNumberWheelChange}
                    value={data.dueDays}
                    name="dueDays"
                    required
                  />
                </div>

          </div>
          <div className={externalInvoiceMode ? "flex flex-col gap-2 xl:col-span-4" : "flex flex-col gap-2"}>
              <label className="w-full text-sm font-normal" htmlFor="invoiceNo">
                <RequiredLabel>Invoice No.</RequiredLabel>
              </label>
              <input
                type="text"
                placeholder="Invoice number"
                className="px-3 py-2.5 focus:outline-none w-full border rounded-lg border-[#053F92] text-[rgba(5, 63, 146, 0.85)]"
                name="invoiceNo"
                value={data.invoiceNo || ""}
                onChange={handleInputChange}
                required
              />
            </div>
          {externalInvoiceMode && (
            <>
              <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 xl:col-span-12">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <label className="text-sm font-normal">
                    <RequiredLabel>Invoice Items</RequiredLabel>
                  </label>
                  <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
                    <div className="flex w-full items-center gap-2 sm:w-auto">
                      <label className="shrink-0 text-xs text-slate-500" htmlFor="externalInvoiceTemplate">
                        Template
                      </label>
                      <select
                        id="externalInvoiceTemplate"
                        value={selectedExternalTemplate}
                        onChange={handleExternalTemplateChange}
                        className="h-10 w-full rounded-lg border border-[#053F92] bg-white px-3 text-sm text-[rgba(5,63,146,0.9)] focus:outline-none sm:w-[230px]"
                      >
                        <option value="">Custom items</option>
                        {EXTERNAL_INVOICE_TEMPLATES.map((template) => (
                          <option key={template.label} value={template.label}>
                            {template.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="button"
                      className="h-10 rounded-md border border-slate-300 bg-white px-3 text-xs text-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
                      onClick={handleClearExternalTemplate}
                      disabled={
                        !selectedExternalTemplate &&
                        (data.items || []).length === 1 &&
                        !(data.items || [])[0]?.description &&
                        !(data.items || [])[0]?.price
                      }
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="hidden px-2 text-xs text-slate-500 sm:grid sm:grid-cols-[minmax(0,1fr)_72px_90px_78px_76px] sm:gap-2">
                    <span>Description</span>
                    <span>Qty</span>
                    <span>Price</span>
                    <span className="text-right">Amount</span>
                    <span />
                  </div>
                  {(data.items || []).map((item, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-1 gap-2 rounded-lg border border-slate-200 bg-white p-2 sm:grid-cols-[minmax(0,1fr)_72px_90px_78px_76px] sm:items-center"
                    >
                      <input
                        type="text"
                        placeholder="Description"
                        className="h-10 min-w-0 rounded-lg border border-[#053F92] px-3 text-[rgba(5,63,146,0.85)] focus:outline-none"
                        value={item.description}
                        onChange={(event) => handleExternalItemChange(index, "description", event.target.value)}
                        maxLength={DESCRIPTION_MAX_LENGTH}
                        required
                      />
                      <input
                        type="number"
                        min={0.01}
                        step="0.01"
                        placeholder="Qty"
                        className="h-10 min-w-0 rounded-lg border border-[#053F92] px-3 text-[rgba(5,63,146,0.85)] focus:outline-none"
                        value={item.qty}
                        onChange={(event) => handleExternalItemChange(index, "qty", event.target.value)}
                        onWheel={preventNumberWheelChange}
                        required
                      />

                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        placeholder="Price"
                        className="h-10 min-w-0 rounded-lg border border-[#053F92] px-3 text-[rgba(5,63,146,0.85)] focus:outline-none"
                        value={item.price}
                        onChange={(event) => handleExternalItemChange(index, "price", event.target.value)}
                        onWheel={preventNumberWheelChange}
                      />

                      <span className="flex h-10 min-w-0 items-center justify-end rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-600">
                        {(Number(item.qty || 0) * Number(item.price || 0)).toFixed(2)}
                      </span>
                      <button
                        type="button"
                        className="h-10 w-full rounded-md border border-slate-300 text-xs text-slate-600 disabled:opacity-50"
                        onClick={() => handleRemoveExternalItem(index)}
                        disabled={(data.items || []).length <= 1}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  className="self-start px-3 py-1.5 rounded-md border border-[#053F92] text-[#053F92] text-xs"
                  onClick={handleAddExternalItem}
                >
                  Add Item
                </button>
              </div>
              <div className="flex flex-col gap-2 xl:col-span-12">
                <label className="w-full text-sm font-normal" htmlFor="description">
                  <RequiredLabel>Description</RequiredLabel>
                </label>
                <textarea
                  type="text"
                  placeholder="Enter short description for invoice"
                  className="h-20 w-full resize-none rounded-lg border border-[#053F92] px-3 py-2.5 text-[rgba(5,63,146,0.85)] focus:outline-none"
                  id="description"
                  name="description"
                  value={data.description}
                  onChange={handleInputChange}
                  maxLength={DESCRIPTION_MAX_LENGTH}
                  required
                />
                <span className="text-xs text-slate-500">
                  {data.description.length}/{DESCRIPTION_MAX_LENGTH}
                </span>
              </div>
              <div className="xl:col-span-12">
                <DiscountInput
                  discountType={externalDiscountType}
                  discountValue={externalDiscountValue}
                  onDiscountTypeChange={handleExternalDiscountTypeChange}
                  onDiscountValueChange={handleExternalDiscountValueChange}
                  currency={data.currency || "USD"}
                  discountAmount={getExternalDiscountData()?.amount || 0}
                />
              </div>
              <div className="grid w-full grid-cols-1 gap-3 md:grid-cols-2 xl:col-span-6">
                <div className="flex flex-col gap-2">
                  <label className="w-full text-sm font-normal" htmlFor="total">
                    Invoice Amount
                  </label>
                  <input
                    type="text"
                    className="px-3 py-2.5 focus:outline-none w-full border rounded-lg border-[#053F92] bg-slate-50 text-slate-500"
                    id="total"
                    name="total"
                    value={Number(data.total || 0).toFixed(2)}
                    readOnly
                    disabled
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="w-full text-sm font-normal" htmlFor="vatPercentage">
                    VAT %
                  </label>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="VAT percentage"
                    className="px-3 py-2.5 focus:outline-none w-full border rounded-lg border-[#053F92] text-[rgba(5,63,146,0.85)]"
                    id="vatPercentage"
                    name="vatPercentage"
                    value={data.vatPercentage}
                    onChange={handleInputChange}
                    onWheel={preventNumberWheelChange}
                  />

                </div>
              </div>
              <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-3 xl:col-span-6">
                <div className="flex flex-col gap-2">
                  <label className="w-full text-sm font-normal" htmlFor="paid">
                    Paid Amount
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={Number(data.total || 0)}
                    step="0.01"
                    placeholder="Paid amount"
                    className="px-3 py-2.5 focus:outline-none w-full border rounded-lg border-[#053F92] text-[rgba(5, 63, 146, 0.85)]"
                    id="paid"
                    name="paid"
                    value={data.paid}
                    onChange={handleInputChange}
                    onWheel={preventNumberWheelChange}
                  />

                </div>
                <div className="flex flex-col gap-2">
                  <label className="w-full text-sm font-normal" htmlFor="externalOutstandingAmount">
                    Outstanding Amount
                  </label>
                  <input
                    type="text"
                    className="px-3 py-2.5 focus:outline-none w-full border rounded-lg border-[#053F92] bg-slate-50 text-slate-500"
                    id="externalOutstandingAmount"
                    value={Math.max(0, Number(data.total || 0) - Number(data.paid || 0)).toFixed(2)}
                    readOnly
                    disabled
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2 xl:col-span-6">
                <label className="w-full text-sm font-normal" htmlFor="status">
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  className="px-3 py-2.5 focus:outline-none w-full border rounded-lg border-[#053F92] text-[rgba(5, 63, 146, 0.85)] bg-white"
                  value={data.status}
                  onChange={handleInputChange}
                >
                  <option value="Not Paid">Not Paid</option>
                  <option value="Paid">Paid</option>
                </select>
              </div>
            </>
          )}
          <>
            {externalInvoiceMode ? (
              <div className="flex flex-col gap-2 xl:col-span-6">
                <label className="w-full text-sm font-normal" htmlFor="currency">
                  <RequiredLabel>Currency</RequiredLabel>
                </label>
                <select
                  id="currency"
                  name="currency"
                  className="px-3 py-2.5 focus:outline-none w-full border rounded-lg border-[#053F92] text-[rgba(5, 63, 146, 0.85)] bg-white"
                  value={data.currency}
                  onChange={handleInputChange}
                  required
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
            ) : (
              <div className="hidden">
                <input
                  name="currency"
                  value={data.currency}
                  readOnly
                />
              </div>
            )}
            <div className="flex flex-col gap-2 xl:col-span-6">
            <div className="flex items-center justify-between gap-2">
              <label className="w-full text-sm font-normal" htmlFor="selectedBankAccountId">
                <RequiredLabel>Bank Account</RequiredLabel>
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="px-3 py-1 rounded-md border border-[#053F92] text-[#053F92] text-xs"
                  onClick={() => setShowBankAccountForm((value) => !value)}
                >
                  {showBankAccountForm ? "Cancel" : "Add"}
                </button>
                <button
                  type="button"
                  className="px-3 py-1 rounded-md border border-slate-300 text-slate-600 text-xs disabled:opacity-50"
                  onClick={handleRemoveBankAccount}
                  disabled={bankAccounts.length <= 1}
                >
                  Remove
                </button>
              </div>
            </div>
            <div className="relative">
              <button
                type="button"
                id="selectedBankAccountId"
                className="flex w-full items-center justify-between gap-3 rounded-lg border border-[#053F92] bg-white px-3 py-2.5 text-left text-[rgba(5,63,146,0.9)] focus:outline-none focus:ring-2 focus:ring-[#053F92]/20"
                onClick={() => setBankDropdownOpen((value) => !value)}
                aria-haspopup="listbox"
                aria-expanded={bankDropdownOpen}
              >
                <span className="min-w-0 flex-1 truncate">
                  {selectedBankAccount.label} - {selectedBankAccount.iban}
                </span>
                <span className={`shrink-0 text-[#053F92] transition-transform ${bankDropdownOpen ? "rotate-180" : ""}`}>
                  &#9662;
                </span>
              </button>
              {bankDropdownOpen && (
                <div
                  className="absolute z-20 mt-1 max-h-44 w-full overflow-y-auto rounded-lg border border-[#053F92]/30 bg-white p-1 shadow-lg"
                  role="listbox"
                >
                  {bankAccounts.map((account) => {
                    const isSelected = account.id === data.selectedBankAccountId;
                    return (
                      <button
                        key={account.id}
                        type="button"
                        className={`flex w-full flex-col rounded-md px-3 py-2 text-left text-sm transition ${
                          isSelected
                            ? "bg-[#053F92] text-white"
                            : "text-slate-800 hover:bg-blue-50"
                        }`}
                        onClick={() => handleSelectBankAccount(account.id)}
                        role="option"
                        aria-selected={isSelected}
                      >
                        <span className="font-medium">{account.label}</span>
                        <span className={isSelected ? "text-white/85" : "text-slate-500"}>
                          {account.iban}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            {showBankAccountForm && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                <input
                  type="text"
                  placeholder="Bank label"
                  className="px-3 py-2 focus:outline-none w-full border rounded-lg border-[#053F92] text-[rgba(5, 63, 146, 0.85)]"
                  name="label"
                  value={newBankAccount.label}
                  onChange={handleNewBankAccountChange}
                />
                <input
                  type="text"
                  placeholder="Account name"
                  className="px-3 py-2 focus:outline-none w-full border rounded-lg border-[#053F92] text-[rgba(5, 63, 146, 0.85)]"
                  name="accountName"
                  value={newBankAccount.accountName}
                  onChange={handleNewBankAccountChange}
                />
                <input
                  type="text"
                  placeholder="IBAN"
                  className="px-3 py-2 focus:outline-none w-full border rounded-lg border-[#053F92] text-[rgba(5, 63, 146, 0.85)]"
                  name="iban"
                  value={newBankAccount.iban}
                  onChange={handleNewBankAccountChange}
                />
                <input
                  type="text"
                  placeholder="SWIFT optional"
                  className="px-3 py-2 focus:outline-none w-full border rounded-lg border-[#053F92] text-[rgba(5, 63, 146, 0.85)]"
                  name="swift"
                  value={newBankAccount.swift}
                  onChange={handleNewBankAccountChange}
                />
                <button
                  type="button"
                  className="md:col-span-2 py-2 rounded-lg bg-[#053F92] text-white"
                  onClick={handleAddBankAccount}
                >
                  Add Bank Account
                </button>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 xl:col-span-6">
            <label
              className="w-full text-sm font-normal"
              htmlFor="orderReference"
            >
              <RequiredLabel>Order reference</RequiredLabel>
            </label>
            <input
              type="text"
              placeholder="Order reference"
              className="px-3 py-2.5 focus:outline-none w-full border rounded-lg border-[#053F92] text-[rgba(5, 63, 146, 0.85)]"
              name="orderReference"
              value={data.orderReference}
              onChange={handleInputChange}
              maxLength={ORDER_REFERENCE_MAX_LENGTH}
              required
            />
            <span className="text-xs text-slate-500">
              {data.orderReference.length}/{ORDER_REFERENCE_MAX_LENGTH}
            </span>
          </div>
          <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-3 xl:col-span-12">
            <div className="flex flex-col gap-2">
              <label
                className="w-full text-sm font-normal"
                htmlFor="collectionLocation"
              >
                Collection
              </label>
              <input
                type="text"
                placeholder="Collection location"
                className="px-3 py-2.5 focus:outline-none w-full border rounded-lg border-[#053F92] text-[rgba(5, 63, 146, 0.85)]"
                id="collectionLocation"
                name="collectionLocation"
                value={data.collectionLocation}
                onChange={handleInputChange}
              />
            </div>
            <div className="flex flex-col gap-2">
              <label
                className="w-full text-sm font-normal"
                htmlFor="deliveryLocation"
              >
                Delivery
              </label>
              <input
                type="text"
                placeholder="Delivery location"
                className="px-3 py-2.5 focus:outline-none w-full border rounded-lg border-[#053F92] text-[rgba(5, 63, 146, 0.85)]"
                id="deliveryLocation"
                name="deliveryLocation"
                value={data.deliveryLocation}
                onChange={handleInputChange}
              />
            </div>
          </div>
          <div className="flex flex-col gap-2 xl:col-span-6">
            <div className="flex min-h-[34px] items-center">
              <label className="text-sm font-normal" htmlFor="externalBillToText">
                <RequiredLabel>Bill To</RequiredLabel>
              </label>
            </div>
            <textarea
              type="text"
              placeholder="Bill to details"
              className="h-28 w-full resize-none rounded-lg border border-[#053F92] px-3 py-2.5 text-[rgba(5,63,146,0.85)] focus:outline-none"
              id="externalBillToText"
              name="externalBillToText"
              value={data.externalBillToText}
              onChange={handleInputChange}
              rows={4}
              maxLength={300}
              required
            />
          </div>
          <div className="flex flex-col gap-2 xl:col-span-6">
            <div className="flex min-h-[34px] items-center justify-between gap-3">
              <label className="text-sm font-normal" htmlFor={externalInvoiceMode ? "serviceRecipientText" : "billToText"}>
                Service Recipient
              </label>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  className="rounded-md border border-[#053F92] px-3 py-1 text-xs text-[#053F92] whitespace-nowrap"
                  onClick={handleCopyBillToFromCustomer}
                >
                  Copy Customer
                </button>
                <button
                  type="button"
                  className="rounded-md border border-slate-300 px-3 py-1 text-xs text-slate-600 whitespace-nowrap"
                  onClick={handleClearBillTo}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              type="text"
              placeholder="Service recipient details"
              className="h-28 w-full resize-none rounded-lg border border-[#053F92] px-3 py-2.5 text-[rgba(5,63,146,0.85)] focus:outline-none"
              id={externalInvoiceMode ? "serviceRecipientText" : "billToText"}
              name={externalInvoiceMode ? "serviceRecipientText" : "billToText"}
              value={externalInvoiceMode ? data.serviceRecipientText : data.billToText}
              onChange={handleInputChange}
              rows={4}
              maxLength={300}
            />
          </div>
          </>
          {!externalInvoiceMode && (
            <div className="flex flex-col gap-2">
              <label className="w-full text-sm font-normal" htmlFor="description">
                <RequiredLabel>Description</RequiredLabel>
              </label>
              <textarea
                type="text"
                placeholder="Short description"
                className="px-3 py-2.5 focus:outline-none w-full border rounded-lg border-[#053F92] text-[rgba(5, 63, 146, 0.85)] resize-none"
                name="description"
                value={data.description}
                onChange={handleInputChange}
                maxLength={DESCRIPTION_MAX_LENGTH}
                required
              />
              <span className="text-xs text-slate-500">
                {data.description.length}/{DESCRIPTION_MAX_LENGTH}
              </span>
            </div>
          )}
          {!externalInvoiceMode && (
            <div className="flex flex-col gap-2">
              <label className="w-full text-sm font-normal" htmlFor="bottomNote">
                Bottom note
              </label>
              <textarea
                type="text"
                placeholder="Optional text at the bottom of invoice"
                className="px-3 py-2.5 focus:outline-none w-full border rounded-lg border-[#053F92] text-[rgba(5, 63, 146, 0.85)] resize-none"
                id="bottomNote"
                name="bottomNote"
                value={data.bottomNote}
                onChange={handleInputChange}
                rows={3}
                maxLength={BOTTOM_NOTE_MAX_LENGTH}
              />
              <span className="text-xs text-slate-500">
                {data.bottomNote.length}/{BOTTOM_NOTE_MAX_LENGTH}
              </span>
            </div>
          )}
          <div className={externalInvoiceMode ? "flex justify-center pt-2 xl:col-span-12" : "flex justify-center pt-2"}>
            <button
              type="submit"
              className="py-3 px-8 rounded-full bg-[#053F92] text-white"
              disabled={loading}
            >
              {loading ? "Generating..." : "Generate Invoice"}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default InvoiceModal;
